import { useState } from 'react';
import { UserRole } from '@klaro7/shared-types';
import { Modal, ModalPortal, ModalOverlay, ModalContent, ModalHeader, ModalTitle, ModalDescription } from '@klaro7/ui';

import { useAuth } from '@/features/auth/hooks/useAuth';
import { useSuspendProfessional, useReactivateProfessional } from '../hooks/useAdminProfessionals';
import type { AdminProfessional } from '../services/professional-admin.service';

export function SuspendProfessionalButton({ pro }: { pro: AdminProfessional }) {
  const { user } = useAuth();
  const isSuperAdmin = user?.role === UserRole.SUPER_ADMIN;
  const [open, setOpen] = useState(false);
  const [reason, setReason] = useState('');
  const suspend = useSuspendProfessional();
  const reactivate = useReactivateProfessional();

  return (
    <div>
      {pro.isSuspended && (
        <div className="mb-3 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-700">
          <p className="font-semibold">
            Sospeso{pro.suspension ? ` il ${new Date(pro.suspension.suspendedAt).toLocaleDateString('it-IT')} da ${pro.suspension.suspendedByName}` : ''}
          </p>
          {pro.suspension?.reason && <p className="mt-0.5">Motivo: {pro.suspension.reason}</p>}
          {isSuperAdmin && (
            <button
              type="button"
              onClick={() => reactivate.mutate(pro.id)}
              disabled={reactivate.isPending}
              className="mt-2 rounded-lg bg-brand-teal px-3 py-1 text-[11px] font-semibold text-white hover:bg-brand-teal-dark disabled:opacity-50"
            >
              Riattiva
            </button>
          )}
        </div>
      )}

      {!pro.isSuspended && isSuperAdmin && (
        <button
          type="button"
          onClick={() => { setReason(''); setOpen(true); }}
          className="rounded-lg bg-red-600 px-3 py-1.5 text-xs font-semibold text-white hover:bg-red-700"
        >
          Sospendi
        </button>
      )}

      {isSuperAdmin && pro.suspensionHistory && pro.suspensionHistory.length > 0 && (
        <ul className="mt-2 space-y-0.5 text-[10px] text-neutral-400">
          {pro.suspensionHistory.slice().reverse().map((h, i) => (
            <li key={i}>
              {h.action === 'suspended' ? 'Sospeso' : 'Riattivato'} · {new Date(h.at).toLocaleString('it-IT')} · {h.byName}{h.reason ? ` · ${h.reason}` : ''}
            </li>
          ))}
        </ul>
      )}

      <Modal open={open} onOpenChange={setOpen}>
        <ModalPortal>
          <ModalOverlay />
          <ModalContent className="max-w-md">
            <ModalHeader>
              <ModalTitle>Sospendi professionista</ModalTitle>
              <ModalDescription>Il professionista non potrà più accedere né ricevere chiamate. Indica il motivo.</ModalDescription>
            </ModalHeader>
            <textarea
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              rows={4}
              placeholder="Motivo della sospensione…"
              className="mt-3 w-full resize-none rounded-lg border border-neutral-200 px-3 py-2 text-sm text-neutral-900 outline-none focus:border-brand-teal/50"
            />
            <div className="mt-4 flex justify-end gap-2">
              <button type="button" onClick={() => setOpen(false)} className="rounded-lg px-3 py-1.5 text-xs text-neutral-500 hover:bg-neutral-100">Annulla</button>
              <button
                type="button"
                disabled={!reason.trim() || suspend.isPending}
                onClick={() => suspend.mutate({ id: pro.id, reason: reason.trim() }, { onSuccess: () => setOpen(false) })}
                className="rounded-lg bg-red-600 px-3 py-1.5 text-xs font-semibold text-white hover:bg-red-700 disabled:opacity-50"
              >
                Sospendi
              </button>
            </div>
          </ModalContent>
        </ModalPortal>
      </Modal>
    </div>
  );
}
